import { useEffect, useState } from "react";
import { Sparkles, ArrowRight, X } from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

type Promo = {
  id: string;
  label: string;
  headline: string;
  cta_text: string;
  cta_link: string;
  ends_at: string;
};

const DISMISS_KEY_PREFIX = "promo_banner_dismissed_";

export default function PromoBanner() {
  const [promo, setPromo] = useState<Promo | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      const { data } = await supabase
        .from("platform_promotions")
        .select("id, label, headline, cta_text, cta_link, ends_at")
        .eq("active", true)
        .gt("ends_at", new Date().toISOString())
        .order("ends_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!alive || !data) return;
      const isDismissed = localStorage.getItem(DISMISS_KEY_PREFIX + data.id) === "1";
      setDismissed(isDismissed);
      setPromo(data as Promo);
    })();
    return () => { alive = false; };
  }, []);

  if (!promo || dismissed) return null;

  const daysLeft = Math.max(0, Math.ceil((new Date(promo.ends_at).getTime() - Date.now()) / 86400000));

  const onDismiss = () => {
    localStorage.setItem(DISMISS_KEY_PREFIX + promo.id, "1");
    setDismissed(true);
  };

  return (
    <div className="w-full bg-gradient-to-r from-primary via-primary to-accent text-primary-foreground">
      <div className="container mx-auto px-4 py-2.5 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 text-center">
        <Sparkles className="h-4 w-4 shrink-0" />
        <p className="text-sm font-medium">
          <span className="font-bold uppercase tracking-wide text-xs mr-2 px-2 py-0.5 rounded-full bg-primary-foreground/20">
            {promo.label}
          </span>
          {promo.headline}
          {daysLeft > 0 && (
            <span className="ml-2 opacity-90 text-xs">· {daysLeft} day{daysLeft === 1 ? "" : "s"} left</span>
          )}
        </p>
        <Link
          to={promo.cta_link}
          className="inline-flex items-center gap-1 text-sm font-semibold underline-offset-2 hover:underline shrink-0"
        >
          {promo.cta_text} <ArrowRight className="h-3.5 w-3.5" />
        </Link>
        <button
          onClick={onDismiss}
          aria-label="Dismiss promo"
          className="absolute right-3 sm:static sm:ml-2 opacity-70 hover:opacity-100"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
